// src/pages/Booking.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSearch } from '../contexts/SearchContext';
import { useLessons } from '../contexts/LessonsContext';
import CourseInfo from '../components/common/CourseInfo';
import Calendar from '../components/calendar/Calendar';
import TimeSelection from '../components/calendar/TimeSelection';
import RequestSummary from '../components/calendar/RequestSummary';
import RequestsList from '../components/requests/RequestsList';
import { BookingProvider, useBooking } from '../contexts/BookingContext';
import { TeacherAvailabilityProvider } from '../contexts/TeacherAvailabilityContext';
import SpinnerOverlay from '../components/ui/SpinnerOverlay';
import Toast from '../components/ui/Toast';

// ─── BookingContent ───────────────────────────────────────────────────────────

const BookingContent = () => {
  const timeSelectionRef = useRef(null);
  const { courseId } = useParams();
  const { getServerTime } = useLessons();

  const [serverNowMs, setServerNowMs] = useState(null);

  const {
    currentMonth,
    prevMonth,
    nextMonth,
    getDaysInMonth,
    getFirstDayOffset,
    handleDateSelect,
    selectedDate,
    availableSlots,
    selectedTimes,
    handleTimeSelect,
    course,
    handleSubmit,
    lessonRequests,
    selectedRequests,
    removeRequest,
    busy,
    alert,
    clearAlert,
  } = useBooking();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [courseId]);

  useEffect(() => {
    if (selectedDate) {
      timeSelectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [selectedDate]);

  useEffect(() => {
    let mounted = true;
    let intervalId = null;

    const syncServerClock = async () => {
      try {
        const serverMs = await getServerTime();
        if (!mounted || !Number.isFinite(serverMs)) return;

        const offset = serverMs - Date.now();

        setServerNowMs(Date.now() + offset);

        intervalId = window.setInterval(() => {
          if (!mounted) return;
          setServerNowMs(Date.now() + offset);
        }, 1000);
      } catch (error) {
        console.error('[Booking] Failed to sync server clock:', error);

        if (!mounted) return;

        setServerNowMs(Date.now());

        intervalId = window.setInterval(() => {
          if (!mounted) return;
          setServerNowMs(Date.now());
        }, 1000);
      }
    };

    syncServerClock();

    return () => {
      mounted = false;
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [getServerTime]);

  return (
    <div className="min-h-screen bg-gray-100" dir="rtl">
      {busy && (
        <SpinnerOverlay
          variant="glow"
          text="נא להמתין"
          dark={false}
          blur={false}
        />
      )}

      <Toast alert={alert} onClose={clearAlert} />
      <CourseInfo course={course} />

      <main className="mx-auto max-w-8xl px-2 sm:px-4 md:px-6 lg:px-8 py-4">
        <div className="flex flex-col lg:grid lg:grid-cols-3 gap-4">
          <div className="order-1 lg:col-span-2">
            <Calendar
              currentMonth={currentMonth}
              prevMonth={prevMonth}
              nextMonth={nextMonth}
              getDaysInMonth={getDaysInMonth}
              getFirstDayOffset={getFirstDayOffset}
              handleDateSelect={handleDateSelect}
              selectedDate={selectedDate}
              availableSlots={availableSlots}
            />
          </div>

          {selectedDate && (
            <div
              className="order-2 lg:order-3 lg:col-span-2"
              ref={timeSelectionRef}
            >
              <TimeSelection
                selectedDate={selectedDate}
                availableSlots={availableSlots}
                selectedTimes={selectedTimes}
                handleTimeSelect={handleTimeSelect}
              />
            </div>
          )}

          <div className="order-3 lg:order-2 lg:row-span-3">
            <RequestSummary
              course={course}
              selectedDate={selectedDate}
              selectedTimes={selectedTimes}
              handleSubmit={handleSubmit}
            />
          </div>

          <div className="order-4 lg:col-span-2">
            <RequestsList
              lessonRequests={lessonRequests}
              selectedRequests={selectedRequests}
              serverNowMs={serverNowMs}
              onRemove={removeRequest}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

// ─── Not Found ────────────────────────────────────────────────────────────────

const CourseNotFound = () => (
  <div
    className="min-h-screen bg-gray-100 flex items-center justify-center px-4"
    dir="rtl"
  >
    <div className="text-center">
      <div className="w-14 h-14 rounded-2xl bg-white border border-gray-200 shadow-sm flex items-center justify-center mx-auto mb-4">
        <svg
          className="w-7 h-7 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
      <h2 className="text-lg font-semibold text-gray-900">הקורס לא נמצא</h2>
      <p className="text-sm text-gray-500 mt-1">
        בדוק את הקישור או חזור לרשימת הקורסים.
      </p>
      <Link
        to="/my-courses"
        className="inline-block mt-4 text-sm text-indigo-600 hover:underline"
      >
        חזרה לקורסים שלי
      </Link>
    </div>
  </div>
);

// ─── Booking (route wrapper) ──────────────────────────────────────────────────

const Booking = () => {
  const { courseId } = useParams();
  const { courses, getMyCoursesActive, getMyCoursesArchived } = useSearch();

  const course =
    (courses ?? []).find((c) => String(c.id) === String(courseId)) ||
    (getMyCoursesActive?.({}, { start: '', end: '' }) ?? []).find(
      (c) => String(c.id) === String(courseId)
    ) ||
    (getMyCoursesArchived?.({}, { start: '', end: '' }) ?? []).find(
      (c) => String(c.id) === String(courseId)
    ) ||
    null;

  if (!course) return <CourseNotFound />;

  const teacherKey = course?.teacher?.id ?? course?.teacher?.email ?? course?.id;

  return (
    <TeacherAvailabilityProvider ownerKey={teacherKey}>
      <BookingProvider course={course}>
        <BookingContent />
      </BookingProvider>
    </TeacherAvailabilityProvider>
  );
};

export default Booking;